import { useAnalytics } from '@/app/analytics/context';
import useAnalyticsMutation from '@/app/mutations/AnalyticsMutation/useAnalyticsMutation';
import useInterval from '@/app/hooks/useInterval';
import { useCallback, useRef, useState } from 'react';

const SYNC_EVERY_MS = 15 * 1000;

// The payload keeps its shape between attempts, so a run of rejections is the
// server saying this meeting's metrics are not wanted — most often a hash it
// cannot resolve. Five is generous for a wobble and quick for a verdict.
const MAX_CONSECUTIVE_FAILURES = 5;

/**
 * Reports call metrics for as long as there is a call to report on.
 *
 * The stop conditions are the point. Left running, this posted every 15 seconds
 * for 21 hours from one abandoned tab, and since each rejected post was then
 * redelivered ten times downstream that came to ~40 error events a minute for a
 * room nobody was in (CAD-6901).
 */
export default function useAnalyticsSync(isEnabled = true) {
  const { data } = useAnalytics();

  const { mutate } = useAnalyticsMutation();

  const failureCountRef = useRef(0);
  const [hasGivenUp, setHasGivenUp] = useState(false);

  const sendDataToServer = useCallback(() => {
    mutate(data, {
      onError() {
        failureCountRef.current += 1;
        if (failureCountRef.current >= MAX_CONSECUTIVE_FAILURES) {
          setHasGivenUp(true);
        }
      },
      onSuccess() {
        failureCountRef.current = 0;
      },
    });
  }, [data, mutate]);

  useInterval(sendDataToServer, SYNC_EVERY_MS, isEnabled && !hasGivenUp);
}
